import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import MuiLanIcon from '@mui/icons-material/Lan';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import MuiSearchIcon from '@mui/icons-material/Search';
import LinearProgress from '@mui/material/LinearProgress';
import ListItemButton from '@mui/material/ListItemButton';
import MuiSearchOffIcon from '@mui/icons-material/SearchOff';
import MuiChevronRightIcon from '@mui/icons-material/ChevronRight';

import type { EscPosCandidate } from 'src/platform/services';

import MuiIcon from 'src/components/mui-icon';

import type { EscPosScanState } from './use-escpos-printer-setup';

// ----------------------------------------------------------------------

interface EscPosDiscoveryPanelProps {
  scan: EscPosScanState;
  port: string;
  savedIds: ReadonlySet<string>;
  currentDefaultId?: string | null;
  onPortChange: (port: string) => void;
  onSearch: () => void;
  onCancel: () => void;
  onChooseInterface: (name: string) => void;
  onSelect: (candidate: EscPosCandidate) => void;
  onAddByAddress: () => void;
}

/** An open port is a candidate; an answered status question makes it identified. */
function reachabilityChip(candidate: EscPosCandidate): { label: string; color: 'success' | 'warning' | 'error' } {
  if (candidate.identified) return { label: 'Answers as ESC/POS', color: 'success' };
  if (candidate.reachable) return { label: 'Port open — unconfirmed', color: 'warning' };
  return { label: 'Not reachable', color: 'error' };
}

function CandidateChips({ candidate, saved, active }: { candidate: EscPosCandidate; saved: boolean; active: boolean }) {
  const reachability = reachabilityChip(candidate);
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
      {active && <Chip label="Active" size="small" color="primary" variant="soft" />}
      {saved && !active && <Chip label="Saved" size="small" color="default" variant="soft" />}
      <Chip label={reachability.label} size="small" color={reachability.color} variant="soft" />
      {candidate.managed && <Chip label="Connected" size="small" color="success" variant="outlined" />}
    </Stack>
  );
}

export default function EscPosDiscoveryPanel({
  scan,
  port,
  savedIds,
  currentDefaultId,
  onPortChange,
  onSearch,
  onCancel,
  onChooseInterface,
  onSelect,
  onAddByAddress,
}: EscPosDiscoveryPanelProps) {
  const searching = scan.phase === 'searching';
  const sweepable = scan.interfaces.filter((adapter) => adapter.swept);

  return (
    <Stack spacing={2} sx={{ px: 3, pb: 2 }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Searches this register’s own network for devices that accept print jobs on the port below. A
          device found here is a candidate until it prints a test page.
        </Typography>
        <TextField
          size="small"
          label="Port"
          value={port}
          onChange={(event) => onPortChange(event.target.value)}
          disabled={searching}
          inputProps={{ inputMode: 'numeric', style: { width: 64 } }}
        />
      </Stack>

      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          disabled={searching}
          onClick={onSearch}
          startIcon={<MuiIcon icon={MuiSearchIcon} />}
        >
          {scan.phase === 'idle' ? 'Scan network' : 'Scan again'}
        </Button>
        {searching && (
          <Button size="small" color="inherit" onClick={onCancel}>
            Stop
          </Button>
        )}
      </Stack>

      {searching && (
        <Box sx={{ py: 1 }}>
          <LinearProgress
            variant={scan.progress && scan.progress.total > 0 ? 'determinate' : 'indeterminate'}
            value={scan.progress && scan.progress.total > 0 ? (100 * scan.progress.completed) / scan.progress.total : 0}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
            {scan.progress && scan.progress.total > 0
              ? `Checked ${scan.progress.completed} of ${scan.progress.total} addresses…`
              : 'Searching for printers…'}
          </Typography>
        </Box>
      )}

      {scan.phase === 'choose-interface' && (
        <Alert severity="info">
          <Typography variant="body2" sx={{ mb: 1 }}>
            This register is on more than one network. Choose which one to search:
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {sweepable.map((adapter) => (
              <Button
                key={`${adapter.name}-${adapter.address}`}
                size="small"
                variant="outlined"
                onClick={() => onChooseInterface(adapter.name)}
              >
                {adapter.name} ({adapter.address}/{adapter.prefixLength})
              </Button>
            ))}
          </Stack>
        </Alert>
      )}

      {scan.phase === 'failed' && (
        <Alert severity="error" action={<Button onClick={onSearch}>Retry</Button>}>
          {scan.error}
        </Alert>
      )}

      {scan.phase === 'done' && scan.notes.length > 0 && (
        <Alert severity={scan.cancelled ? 'warning' : 'info'}>{scan.notes.join(' ')}</Alert>
      )}

      {scan.phase === 'done' && scan.candidates.length > 0 && (
        <List disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
          {scan.candidates.map((candidate) => {
            const id = `escpos:${candidate.ipAddress}:${String(candidate.port)}`;
            return (
              <ListItem key={id} disablePadding divider>
                <ListItemButton onClick={() => onSelect(candidate)} sx={{ py: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.lighter', color: 'primary.main', mr: 2 }}>
                    <MuiIcon icon={MuiLanIcon} width={22} />
                  </Avatar>
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography variant="subtitle2">
                        {candidate.ipAddress}:{candidate.port}
                      </Typography>
                    }
                    secondary={
                      <CandidateChips
                        candidate={candidate}
                        saved={savedIds.has(id)}
                        active={currentDefaultId === id}
                      />
                    }
                  />
                  <MuiIcon icon={MuiChevronRightIcon} sx={{ color: 'text.disabled' }} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

      {scan.phase === 'done' && scan.candidates.length === 0 && (
        <Stack alignItems="center" spacing={1} sx={{ py: 3, textAlign: 'center' }}>
          <MuiIcon icon={MuiSearchOffIcon} width={40} sx={{ color: 'text.disabled' }} />
          <Typography variant="subtitle1">Nothing answered on port {port}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            Check the printer is switched on and on the same network as this register. If it is on a
            different network, or you know its address, add it by IP address.
          </Typography>
          <Button variant="contained" onClick={onAddByAddress} sx={{ mt: 1 }}>
            Add by IP address
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
